import { Calendar, CreditCard, Euro } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { PaymentScheduleForm } from "./PaymentScheduleForm";

interface PaymentComponentProps {
  project: any;
  isEditingInline: boolean;
  paymentSchedule: any;
  onPaymentScheduleChange: (schedule: any) => void;
  title?: string;
  projectValue?: number;
}

export function PaymentComponent({ 
  project, 
  isEditingInline, 
  paymentSchedule, 
  onPaymentScheduleChange,
  title = "Invoicing Schedule",
  projectValue
}: PaymentComponentProps) {
  
  // Use explicit projectValue if provided, otherwise fall back to project.sold_for
  const effectiveProjectValue = projectValue !== undefined ? projectValue : (project.sold_for ? parseFloat(project.sold_for) : 0);
  
  if (isEditingInline) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="h-5 w-5 mr-2" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PaymentScheduleForm
            value={paymentSchedule}
            onChange={onPaymentScheduleChange}
            projectValue={effectiveProjectValue || undefined}
            projectStartDate={project.start_date}
            hideHeader={true}
          />
        </CardContent>
      </Card>
    );
  }

  if (!paymentSchedule) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="h-5 w-5 mr-2" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            No payment schedule configured
          </p>
        </CardContent>
      </Card>
    );
  }

  const renderPaymentDetails = () => {
    switch (paymentSchedule.type) {
      case 'once_off':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">Payment Type:</span>
              <Badge variant="outline">One-time Payment</Badge>
            </div>
            {paymentSchedule.invoicing_date && (
              <div className="flex items-center justify-between">
                <span className="font-medium">Invoicing Date:</span>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{format(new Date(paymentSchedule.invoicing_date), 'PPP')}</span>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="font-medium">Amount:</span>
              <span className="font-bold text-lg">€{effectiveProjectValue.toLocaleString()}</span>
            </div>
          </div>
        );

      case 'split':
        const splits = paymentSchedule.splits || [];
        const totalPercentage = splits.reduce((sum: number, split: any) => sum + (split.percentage || 0), 0);
        
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">Payment Type:</span>
              <Badge variant="outline">Split Payment</Badge>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground">Payment Splits:</h4>
              {splits.map((split: any, index: number) => (
                <div key={index} className="p-3 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Split {index + 1}</span>
                    <Badge variant="secondary">{split.percentage}%</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Trigger:</span>
                      <span className="ml-2 font-medium">
                        {split.trigger === 'at_start' ? 'At Start' : 
                         split.trigger === 'at_delivery' ? 'At End' : 
                         split.trigger === 'custom' ? 'Custom Date' : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="ml-2 font-medium">
                        €{((effectiveProjectValue * (split.percentage || 0)) / 100).toLocaleString()}
                      </span>
                    </div>
                    {split.date && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="ml-2 font-medium">{format(new Date(split.date), 'PPP')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between">
                <span className="font-medium">Total Coverage:</span>
                <Badge variant={totalPercentage === 100 ? "default" : "destructive"}>
                  {totalPercentage}%
                </Badge>
              </div>
            </div>
          </div>
        );

      case 'recurring':
        const frequency = paymentSchedule.frequency;
        const amount = paymentSchedule.amount_per_interval;
        const startDateType = paymentSchedule.start_date_type || 'project_start';
        const paymentTerms = paymentSchedule.payment_terms || '60_days';
        
        const frequencyLabels: Record<string, string> = {
          monthly: 'Monthly',
          bimonthly: 'Bimonthly (2m)',
          quarterly: 'Quarterly (3m)',
          semester: 'Semester (6m)'
        };

        const paymentTermsLabels: Record<string, string> = {
          immediately: 'Immediately',
          '30_days': '30 days',
          '60_days': '60 days',
          '90_days': '90 days',
          '120_days': '120 days'
        };
        
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">Payment Type:</span>
              <Badge variant="outline">Recurring Payment</Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="font-medium">Frequency:</span>
              <span className="capitalize font-medium">{frequencyLabels[frequency] || 'N/A'}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="font-medium">Amount per {frequency}:</span>
              <span className="font-bold text-lg">€{(amount || 0).toLocaleString()}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-medium">Start Date Type:</span>
              <span className="font-medium">{startDateType === 'project_start' ? 'Project Start Date' : 'Custom Date'}</span>
            </div>
            
            {paymentSchedule.start_date && (
              <div className="flex items-center justify-between">
                <span className="font-medium">Start Date:</span>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{format(new Date(paymentSchedule.start_date), 'PPP')}</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="font-medium">Payment Terms:</span>
              <span className="font-medium">{paymentTermsLabels[paymentTerms] || 'N/A'}</span>
            </div>
          </div>
        );

      default:
        return (
          <p className="text-muted-foreground text-center py-4">
            Unknown payment schedule type
          </p>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <CreditCard className="h-5 w-5 mr-2" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {renderPaymentDetails()}
      </CardContent>
    </Card>
  );
}
